/**
 * 真人人像库专用接口
 * 文档：https://docs.api.33dd.cn/zh/api/management/火山人像库
 */

import { del, get, post } from '../request';
import { PORTRAIT_GROUP_TYPE_LIVENESS_FACE } from '../utils/seedanceConstants';
import {
  buildVirtualPortraitAuthHeaders,
  type VirtualPortraitRequestOptions,
} from './virtualPortrait';

const BASE_URL = process.env.REACT_APP_BASE_URL || '/proxy/base';

export type RealPersonRequestOptions = VirtualPortraitRequestOptions;

export interface RealPersonCreateSessionBody {
  callback_url: string;
}

export interface RealPersonGetResultBody {
  byted_token: string;
}

export interface RealPersonBindGroupBody {
  group_id: string;
}

const rpRequestConfig = (options: RealPersonRequestOptions) => {
  const baseURL = options.baseURL ?? BASE_URL;
  const headers = buildVirtualPortraitAuthHeaders(options);
  options.onDebug?.('Headers: Authorization, New-Api-User');
  return {
    baseURL,
    headers,
    responseType: 'json' as const,
  };
};

/** — 发起真人像上传会话 */
export const createRealPersonSession = async <T = any>(
  body: RealPersonCreateSessionBody,
  options: RealPersonRequestOptions,
) => {
  const path = '/seedance/assets/?Action=CreateVisualValidateSession&Version=2024-01-01';
  options.onDebug?.(`POST ${path}`);
  
  const data = {
    CallbackURL: body.callback_url,
  };

  const res = await post<any>(path, data, rpRequestConfig(options));
  
  if (res?.ResponseMetadata?.Error) {
    throw new Error(res.ResponseMetadata.Error.Message || '发起真人像认证会话失败');
  }
  
  return res as T;
};

/** POST /api/doubao/seedance/real_person/get_result — 查询真人像上传结果 */
export const getRealPersonSessionResult = async <T = any>(
  body: RealPersonGetResultBody,
  options: RealPersonRequestOptions,
) => {
  const path = '/seedance/assets/?Action=GetVisualValidateResult&Version=2024-01-01';
  options.onDebug?.(`POST ${path}`);
  const data = {
    BytedToken: body.byted_token,
    ProjectName: 'default',
  };
  const res = await post<any>(path, data, rpRequestConfig(options));
  if (res?.ResponseMetadata?.Error) {
    throw new Error(res.ResponseMetadata.Error.Message || '查询认证结果失败');
  }
  return res as T;
};

/** POST /api/doubao/seedance/real_person/group — 用户绑定真人素材组 */
export const bindRealPersonGroup = <T = unknown>(
  body: RealPersonBindGroupBody,
  options: RealPersonRequestOptions,
) => {
  options.onDebug?.(`POST /api/doubao/seedance/real_person/group group_id=${body.group_id}`);
  return post<T, RealPersonBindGroupBody>('/api/doubao/seedance/real_person/group', body, rpRequestConfig(options));
};

/** — 查询用户已绑定的真人素材组列表 */
export const listRealPersonGroups = async <T = any>(options: RealPersonRequestOptions) => {
  const path = '/seedance/assets/?Action=ListAssetGroups&Version=2024-01-01';
  options.onDebug?.(`POST ${path}`);
  const data = {
    Filter: {
      GroupType: 'LivenessFace',
    },
    PageNumber: 1,
    PageSize: 10,
    SortBy: 'CreateTime',
    SortOrder: 'Desc',
    ProjectName: 'default',
  };
  const res = await post<any>(path, data, {
    ...rpRequestConfig(options),
    cache: 'no-store',
  });
  if (res?.ResponseMetadata?.Error) {
    throw new Error(res.ResponseMetadata.Error.Message || '获取真人素材组列表失败');
  }
  return res as T;
};

export interface RealPersonPortraitListQuery {
  page_number?: number;
  page_size?: number;
  name?: string;
}

export interface RealPersonCreatePortraitBody {
  group_id: string;
  url: string;
  name: string;
  asset_type: string;
}

/** — 查询真人素材组下的人像素材列表 */
export const listRealPersonPortraits = async <T = any>(
  groupId: string,
  options: RealPersonRequestOptions,
  listQuery?: RealPersonPortraitListQuery,
) => {
  const data: any = {
    Filter: {
      GroupIds: [groupId],
      GroupType: 'LivenessFace',
    },
    PageNumber: 1,
    PageSize: 100,
    SortBy: 'CreateTime',
    SortOrder: 'Desc',
  };
  
  if (listQuery?.page_number != null && listQuery.page_number > 0) {
    data.PageNumber = Math.floor(listQuery.page_number);
  }
  if (listQuery?.page_size != null && listQuery.page_size > 0) {
    data.PageSize = Math.floor(listQuery.page_size);
  }

  const path = '/seedance/assets/?Action=ListAssets&Version=2024-01-01';
  options.onDebug?.(`POST ${path}`);
  
  const res = await post<any>(path, data, {
    ...rpRequestConfig(options),
    cache: 'no-store',
    ...(options.signal ? { signal: options.signal } : {}),
  });
  
  if (res?.ResponseMetadata?.Error) {
    throw new Error(res.ResponseMetadata.Error.Message || '获取真人素材列表失败');
  }
  
  return res as T;
};

/** — 上传真人素材组人像素材 */
export const createRealPersonPortrait = async <T = any>(
  body: RealPersonCreatePortraitBody,
  options: RealPersonRequestOptions,
) => {
  const path = '/seedance/assets/?Action=CreateAsset&Version=2024-01-01';
  options.onDebug?.(`POST ${path}`);
  const data = {
    GroupId: body.group_id,
    URL: body.url,
    AssetType: body.asset_type,
    ...(body.name ? { Name: body.name } : {}),
  };
  const res = await post<any>(path, data, {
    ...rpRequestConfig(options),
  });
  if (res?.ResponseMetadata?.Error) {
    throw new Error(res.ResponseMetadata.Error.Message || '上传素材失败');
  }
  return res as T;
};

/** — 删除人像素材 */
export const deleteRealPersonPortrait = async <T = any>(
  assetId: string,
  groupId: string,
  options: RealPersonRequestOptions,
) => {
  const path = `/seedance/assets/?Action=DeleteAsset&Version=2024-01-01`;
  options.onDebug?.(`POST ${path}`);
  const res = await post<any>(path, { Id: assetId }, {
    ...rpRequestConfig(options),
  });
  if (res?.ResponseMetadata?.Error) {
    throw new Error(res.ResponseMetadata.Error.Message || '删除素材失败');
  }
  return res as T;
};
